import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  useEffect(() => {
    // Load Bootstrap CSS if not already loaded
    if (!document.querySelector('link[href*="bootstrap"]')) {
      const bootstrapCSS = document.createElement('link');
      bootstrapCSS.rel = 'stylesheet';
      bootstrapCSS.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css';
      document.head.appendChild(bootstrapCSS);
    }

    // Load Bootstrap JS if not already loaded
    if (!window.bootstrap) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js';
      script.onload = () => {
        initializeCarousel();
      };
      document.head.appendChild(script);
    } else {
      initializeCarousel();
    }

    function initializeCarousel() {
      // Scroll indicator
      const scrollIndicator = document.getElementById('scrollIndicator');
      if (scrollIndicator) {
        window.addEventListener('scroll', () => {
          const scrolled = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
          scrollIndicator.style.width = scrolled + '%';
        });
      }

      // Dynamic word change for carousel with proper spacing
      const carousel = document.getElementById('heroImageCarousel');
      const dynamicWord = document.getElementById('dynamicWord');

      if (carousel && dynamicWord) {
        carousel.addEventListener('slid.bs.carousel', function () {
          const activeItem = carousel.querySelector('.carousel-item.active');
          let newWord = activeItem.getAttribute('data-word') || '';
          // Remove unwanted words if present
          newWord = newWord.replace(/assurance|insurance/gi, '').trim();
          
          // Set the dynamic word with proper spacing
          dynamicWord.textContent = newWord;
          
          // Add CSS for consistent width and alignment
          dynamicWord.style.display = 'inline-block';
          dynamicWord.style.minWidth = '120px'; // Fixed width for consistent spacing
          dynamicWord.style.textAlign = 'left';
        });

        // Initialize Bootstrap carousel
        if (window.bootstrap) {
          new window.bootstrap.Carousel(carousel, {
            interval: 3000,
            ride: 'carousel'
          });
        }
      }

      // Rest of the initialization code...
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      }, observerOptions);

      const animatedElements = document.querySelectorAll('.value-card, .plan-card, .stat-item, .testimonial-card, .blog-card, .step-item');
      animatedElements.forEach(el => observer.observe(el));

      // Animated counters
      const counters = document.querySelectorAll('.stat-number');
      const counterObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const counter = entry.target;
            const target = parseInt(counter.getAttribute('data-target'));
            const duration = 2000;
            const step = target / (duration / 16);
            let current = 0;

            const updateCounter = () => {
              if (current < target) {
                current += step;
                counter.textContent = Math.floor(current).toLocaleString();
                requestAnimationFrame(updateCounter);
              } else {
                counter.textContent = target.toLocaleString();
              }
            };

            updateCounter();
            counterObserver.unobserve(counter);
          }
        });
      });

      counters.forEach(counter => counterObserver.observe(counter));

      // Simple AOS implementation
      const elements = document.querySelectorAll('[data-aos]');
      const aosObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      elements.forEach(el => aosObserver.observe(el));

      // Value Card Overlay functionality
      const grid = document.querySelector('.values-grid');
      const overlay = document.getElementById('valueCardOverlay');
      if (grid && overlay) {
        const closeBtn = overlay.querySelector('.vc-close');
        const backdrop = overlay.querySelector('.vc-backdrop');
        const content = overlay.querySelector('.vc-content');

        function openOverlay(card) {
          const clone = card.cloneNode(true);
          content.innerHTML = '';
          content.append(...Array.from(clone.childNodes));
          overlay.classList.add('active');
          document.body.classList.add('vc-open');
          grid.classList.add('blur-bg');
          closeBtn.focus();
        }

        function closeOverlay() {
          overlay.classList.remove('active');
          document.body.classList.remove('vc-open');
          grid.classList.remove('blur-bg');
        }

        grid.querySelectorAll('.value-card').forEach(card => {
          card.style.cursor = 'pointer';
          card.addEventListener('click', () => openOverlay(card));
        });

        closeBtn.addEventListener('click', closeOverlay);
        backdrop.addEventListener('click', closeOverlay);
        document.addEventListener('keydown', e => {
          if (e.key === 'Escape' && overlay.classList.contains('active')) {
            closeOverlay();
          }
        });
      }

      // Back to Top Button
      const backToTop = document.getElementById("backToTop");
      if (backToTop) {
        window.onscroll = function () {
          if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
            backToTop.style.display = "block";
          } else {
            backToTop.style.display = "none";
          }
        };

        backToTop.addEventListener("click", () => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      }
    }
  }, []);

  return (
    <>
      {/* Scroll Indicator */}
      <div className="scroll-indicator" id="scrollIndicator"></div>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="highlight1" id="dynamicWord" style={{
                display: 'inline-block',
                minWidth: '120px',
                textAlign: 'left'
              }}>Life</span>
              <span id="insauranceText"> Insurance with </span>
              <span className="highlight">Assurance</span>
            </h1>
            <p className="hero-subtitle">
              Protecting what matters most to you with trust, care, and unwavering
              commitment
            </p>
            <div className="hero-buttons">
              <Link to="/plans" className="btn-primary">Explore Plans</Link>
              <Link to="/about" className="btn-secondary">Learn More</Link>
            </div>
          </div>

          {/* Image Slider */}
          <div className="hero-slider">
            <div
              id="heroImageCarousel"
              className="carousel slide"
              data-bs-ride="carousel"
              data-bs-interval="3000"
            >
              {/* Carousel Inner */}
              <div className="carousel-inner">
                <div className="carousel-item active" data-word="Life">
                  <img
                    src="assets/356.jpg"
                    alt="Life Insurance Protection"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Health">
                  <img
                    src="assets/361.jpg"
                    alt="Health Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Car">
                  <img
                    src="assets/349.jpg"
                    alt="Car Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Marine">
                  <img
                    src="assets/353.jpg"
                    alt="Marine Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Fire">
                  <img
                    src="assets/359.jpg"
                    alt="Fire Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Workmen's Compensation">
                  <img
                    src="assets/357.jpg"
                    alt="Workmen's Compensation Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Commercial Vehicle">
                  <img
                    src="assets/350.jpg"
                    alt="Commercial Vehicle Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Doctor">
                  <img
                    src="assets/420.jpg"
                    alt="Doctor Insurance"
                    className="d-block w-100"
                  />
                </div>
                <div className="carousel-item" data-word="Cyber">
                  <img
                    src="assets/430.jpg"
                    alt="Cyber Insurance"
                    className="d-block w-100"
                  />
                </div>
              </div>

              {/* Carousel Indicators */}
              <div className="carousel-indicators">
                {[0,1,2,3,4,5,6,7,8].map((index) => (
                  <button
                    key={index}
                    type="button"
                    data-bs-target="#heroImageCarousel"
                    data-bs-slide-to={index}
                    className={index === 0 ? "active" : ""}
                    aria-current={index === 0 ? "true" : undefined}
                    aria-label={`Slide ${index + 1}`}
                  ></button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="about">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose WE CARE ASSURANCE?</h2>
            <p className="section-subtitle">
              Built on the foundation of Trust, Assurance, and Care
            </p>
          </div>
          <div className="values-grid">
            <div className="value-card" data-aos="fade-up">
              <div className="value-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L3 7L12 12L21 7L12 2Z"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <path d="M3 17L12 22L21 17" stroke="#0057A4" strokeWidth="2" />
                  <path d="M3 12L12 17L21 12" stroke="#0057A4" strokeWidth="2" />
                </svg>
              </div>
              <h3>Trust</h3>
              <p>
                Over 23 years of reliable service and transparent dealings with
                our valued customers
              </p>
            </div>
            <div className="value-card" data-aos="fade-up" data-aos-delay="100">
              <div className="value-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 22S2 16 2 10V5L12 2L22 5V10C22 16 12 22 12 22Z"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Assurance</h3>
              <p>Comprehensive coverage with guaranteed claims settlement</p>
            </div>
            <div className="value-card" data-aos="fade-up" data-aos-delay="200">
              <div className="value-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20.84 4.61A5.5 5.5 0 0 0 16.5 2C13.76 2 12 3.64 12 3.64S10.24 2 7.5 2A5.5 5.5 0 0 0 3.16 4.61C2.42 7.55 3.14 12.22 12 21.5C20.86 12.22 21.58 7.55 20.84 4.61Z"
                    stroke="#E74C3C"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Care</h3>
              <p>
                Personal attention to every customer with dedicated support and
                care at every step
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Value Card Overlay */}
      <div id="valueCardOverlay" className="vc-overlay" aria-hidden="true">
        <div className="vc-backdrop"></div>
        <div
          className="vc-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vcTitle"
        >
          <button className="vc-close" aria-label="Close">×</button>
          <div className="vc-content"></div>
        </div>
      </div>

      {/* Plans Section */}
      <section className="plans-section" id="plans">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">We are offering...</h2>
            <p className="section-subtitle">
              Choose the perfect coverage for your needs
            </p>
          </div>
          <div className="plans-grid">
            {/* Personal Line */}
            <div className="plan-card" data-aos="fade-up">
              <div className="plan-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 21V19C20 16.79 18.21 15 16 15H8C5.79 15 4 16.79 4 19V21"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Personal Line</h3>
              <p>
                Protect yourself and your family with tailored personal insurance
                solutions
              </p>
              <ul className="plan-features">
                <li>✓ Health & Super Top-up Protection</li>
                <li>✓ Life Insurance</li>
                <li>✓ Travel & Personal Accident Insurance</li>
              </ul>
              <Link to="/plans#pl" className="plan-btn">Learn More</Link>
            </div>

            {/* Corporate Line */}
            <div className="plan-card" data-aos="fade-up" data-aos-delay="100">
              <div className="plan-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 14S9.5 16 12 16S16 14 16 14"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <line
                    x1="9"
                    y1="9"
                    x2="9.01"
                    y2="9"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                  <line
                    x1="15"
                    y1="9"
                    x2="15.01"
                    y2="9"
                    stroke="#0057A4"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Corporate Line</h3>
              <p>
                Comprehensive protection for businesses, employees, and corporate
                assets
              </p>
              <ul className="plan-features">
                <li>✓ Employee Health & Group Insurance</li>
                <li>✓ Property & Liability Coverage</li>
                <li>✓ Customized Corporate Solutions</li>
              </ul>
              <Link to="/plans#cl" className="plan-btn">Learn More</Link>
            </div>

          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item" data-aos="fade-up">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
              </div>
              <div className="stat-number" data-target="8000">
                0<span style={{color: '#e74c3c'}}>+</span>
              </div>
              <div className="stat-label">Happy Customers</div>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="100">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"
                  />
                </svg>
              </div>
              <div className="stat-number" data-target="22">
                0<span style={{color: '#e74c3c'}}>+</span>
              </div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="200">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M19,3H18V1H16V3H8V1H6V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19Z"
                  />
                </svg>
              </div>
              <div className="stat-number" data-target="1000">
                0<span style={{color: '#e74c3c'}}>+</span>
              </div>
              <div className="stat-label">Claims Settled</div>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="300">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                  />
                </svg>
              </div>
              <div className="stat-number" data-target="8000">
                0<span style={{color: '#e74c3c'}}>+</span>
              </div>
              <div className="stat-label">Active Policies</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2>Ready to Get Protected?</h2>
            <p>
              Join thousands of satisfied customers who trust WE CARE ASSURANCE
              for their insurance needs
            </p>
            <div className="cta-buttons">
              <Link to="/plans" className="btn-primary">Get Quote Now</Link>
              <Link to="/contact" className="btn-secondary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button id="backToTop" title="Go to top">↑</button>
    </>
  );
};

export default Home;
